import type { ReviewRun } from '@/data/reviews/use-review-statuses';
import type { PrV2AvailableLabel } from '@/lib/pr-v2/types';
import type { PullRequest } from '@/lib/pull-requests/types';
import type { ReviewRunStatus } from '@/lib/realtime/types';
import type { Reviewer, ReviewerStateValues } from '@/types/github';

import type { PrV2Label, PrV2MetadataSummary } from '../shared/pr-v2-metadata';

import { isTimedOutPendingRun } from '@/data/reviews/review-run-optimistic';
import { CUBIC_BOT_NAMES } from '@/lib/constants';

// Pure helpers shared by the PR page sidebar and the /code PR tab's properties list.

type SidebarLabel = NonNullable<PullRequest['labels']>[number];
export type CubicStatus = 'running' | 'failed' | 'issues' | 'clean' | 'idle';
export type CubicRunStatus = {
  status: CubicStatus;
  tooltip: string;
  runningTraceIds: string[];
  progressPercentage?: number;
  progressMessage?: string;
};

const CUBIC_REVIEWER_LOGINS = new Set(CUBIC_BOT_NAMES.map((login) => login.toLowerCase()));
const FAILURE_RUN_STATUSES: Set<ReviewRunStatus> = new Set(['failed', 'timeout', 'cancelled']);

function toTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function isCubicReviewer(login: string | null): boolean {
  return login ? CUBIC_REVIEWER_LOGINS.has(login.toLowerCase()) : false;
}

/**
 * The reviewer shape the reviewer picker and reviewer rows take. Review ids go through Number so
 * a missing or zero id is dropped rather than passed on.
 */
export function toGithubReviewer(reviewer: {
  login: string;
  avatarUrl: string | null;
  state: ReviewerStateValues;
  reviewId: number | string | null;
}): Reviewer {
  const reviewId = reviewer.reviewId == null ? null : Number(reviewer.reviewId);

  return {
    login: reviewer.login,
    avatarUrl: reviewer.avatarUrl ?? '',
    state: reviewer.state,
    reviewId: reviewId ? String(reviewId) : undefined,
  };
}

/** A label color as hex digits without '#', grey for a label stored without one. */
export function toLabelHexColor(color: string | null): string {
  return (color ?? '71717a').replace('#', '');
}

export function buildPrV2Labels(metadata: PrV2MetadataSummary): SidebarLabel[] {
  return metadata.labels.map((label, index) => ({
    id: `pr-v2-label-${index}-${label.name}`,
    name: label.name,
    color: toLabelHexColor(label.color),
    description: '',
    isDefault: false,
    url: '',
  }));
}

/**
 * The label picker's options: the pull request's labels first, in their order, then the
 * repository's other labels by name. A label missing from the repository list, such as one
 * deleted since it was applied, stays listed so it can be removed.
 */
export function buildPrV2LabelOptions(
  prLabels: PrV2Label[],
  availableLabels: PrV2AvailableLabel[],
): PrV2AvailableLabel[] {
  const availableByName = new Map<string, PrV2AvailableLabel>();
  for (const label of availableLabels) {
    const nameKey = label.name.toLowerCase();
    if (!availableByName.has(nameKey)) {
      availableByName.set(nameKey, label);
    }
  }

  const prLabelNames = new Set<string>();
  const appliedOptions: PrV2AvailableLabel[] = [];
  for (const label of prLabels) {
    const nameKey = label.name.toLowerCase();
    if (!prLabelNames.has(nameKey)) {
      prLabelNames.add(nameKey);
      appliedOptions.push(availableByName.get(nameKey) ?? { ...label, description: null });
    }
  }

  const otherOptions = Array.from(availableByName.values())
    .filter((label) => !prLabelNames.has(label.name.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

  return [...appliedOptions, ...otherOptions];
}

export function compareReviewRunsByRecency(a: ReviewRun, b: ReviewRun): number {
  const updatedAtDiff = toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);

  if (updatedAtDiff !== 0) {
    return updatedAtDiff;
  }

  return toTimestamp(b.createdAt) - toTimestamp(a.createdAt);
}

/**
 * Status precedence for the cubic row:
 * running > failed > issues > clean > idle.
 */
export function deriveCubicRunStatus(reviewRuns: ReviewRun[]): CubicRunStatus {
  const activeRunningRuns = reviewRuns.filter(
    (run) => run.status === 'running' && !isTimedOutPendingRun(run),
  );
  const runningTraceIds = Array.from(
    new Set(
      activeRunningRuns
        .map((run) => run.traceId)
        .filter((traceId): traceId is string => traceId.length > 0),
    ),
  );

  if (activeRunningRuns.length > 0) {
    // Pick progress from the first running general run (primary), falling back to any running run
    const primaryRunningRun =
      activeRunningRuns.find((run) => run.runKind === 'general') ?? activeRunningRuns[0];

    return {
      status: 'running',
      tooltip: primaryRunningRun?.progressMessage ?? 'Review in progress',
      runningTraceIds,
      progressPercentage: primaryRunningRun?.progressPercentage,
      progressMessage: primaryRunningRun?.progressMessage,
    };
  }

  const runsByRecency = [...reviewRuns].sort(compareReviewRunsByRecency);
  const failedRun = runsByRecency.find((run) => FAILURE_RUN_STATUSES.has(run.status));

  if (failedRun) {
    return {
      status: 'failed',
      tooltip:
        failedRun.status === 'timeout'
          ? 'Review timed out'
          : failedRun.status === 'cancelled'
            ? 'Review canceled'
            : 'Review failed',
      runningTraceIds,
    };
  }

  const runWithIssues = runsByRecency.find(
    (run) => run.status === 'completed' && run.violationCount > 0,
  );

  if (runWithIssues) {
    return {
      status: 'issues',
      tooltip: `${runWithIssues.violationCount} issue${runWithIssues.violationCount === 1 ? '' : 's'} found in latest cubic review`,
      runningTraceIds,
    };
  }

  const cleanRun = runsByRecency.find(
    (run) => (run.status === 'completed' && run.violationCount === 0) || run.status === 'skipped',
  );

  if (cleanRun) {
    return {
      status: 'clean',
      tooltip: 'No issues found',
      runningTraceIds,
    };
  }

  return {
    status: 'idle',
    tooltip: 'General AI review did not run on this PR',
    runningTraceIds,
  };
}
